import { Dialog } from '@eppendorf/vnls-react-components';
import {
  FunctionComponentElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useFeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import { UnsavedDialog } from '$features/notifications/add-or-edit-notification-rule-dialog/edit-notification-rule/unsaved-dialog/unsaved-dialog';
import { NotificationDialogProgressIndicator } from '$features/notifications/add-or-edit-notification-rule-dialog/notification-dialog-progress-indicator';
import { NotificationRuleDialogContent } from '$features/notifications/add-or-edit-notification-rule-dialog/notification-rule-dialog-content';
import { SuccessScreen } from '$features/notifications/add-or-edit-notification-rule-dialog/screens/success-screen';
import { useNotificationRuleDialogForm } from '$features/notifications/add-or-edit-notification-rule-dialog/useNotificationRuleDialogForm';
import {
  AddOrEditNotiRuleModalScreens,
  NotificationRuleDetailData,
  NotificationRuleDialogContentHandle,
  NotificationRuleMode,
} from '$features/notifications/types';

export interface EditNotificationRuleDialogProps {
  isOpen?: boolean;
  onOpenChange: (open: boolean) => void;
  data: NotificationRuleDetailData;
}

type EditNotiRuleDialogScreen = AddOrEditNotiRuleModalScreens | 'unsavedChangesScreen';

export function EditNotificationRuleDialog({
  isOpen = false,
  onOpenChange,
  data,
}: EditNotificationRuleDialogProps): FunctionComponentElement<EditNotificationRuleDialogProps> {
  const { t } = useTranslation();
  const { handleFormSubmit, isLoading, requestError, isSuccess, formMethods } =
    useNotificationRuleDialogForm(data, NotificationRuleMode.EDIT);
  const isNotificationTypesEnabled = useFeatureIsEnabled(
    'ESEVNLS_4596_NOTIFICATION_TYPES',
  );
  const [activeScreen, setActiveScreen] =
    useState<EditNotiRuleDialogScreen>('deviceTypeScreen');
  const [screenBeforeUnsaved, setScreenBeforeUnsaved] =
    useState<EditNotiRuleDialogScreen>('deviceTypeScreen');
  const dialogContentRef = useRef<NotificationRuleDialogContentHandle>(null);
  // Needs to be desctructed during render, otherwise would be stale.
  // Also see https://react-hook-form.com/docs/useform/formstate -> Rules
  const { isDirty } = formMethods.formState;

  useEffect(() => {
    if (isSuccess) {
      setActiveScreen('successScreen');
    }
  }, [isSuccess]);

  function handleOpenChange(open: boolean): void {
    if (
      !open &&
      isDirty &&
      activeScreen !== 'successScreen' &&
      activeScreen !== 'unsavedChangesScreen'
    ) {
      setScreenBeforeUnsaved(activeScreen);
      setActiveScreen('unsavedChangesScreen');
      return;
    }
    if (onOpenChange) {
      onOpenChange(open);
    }
  }

  const handleContinueEditing = () => {
    setActiveScreen(screenBeforeUnsaved);
  };
  const isFormScreen =
    activeScreen !== 'successScreen' && activeScreen !== 'unsavedChangesScreen';

  const handleStepClick = useCallback((screen: AddOrEditNotiRuleModalScreens) => {
    dialogContentRef.current?.goToScreen(screen);
  }, []);

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={(open) => handleOpenChange(open)}
      className={
        activeScreen !== 'successScreen' &&
        activeScreen !== 'unsavedChangesScreen' &&
        activeScreen !== 'errorScreen'
          ? 'w-max-7xl'
          : 'w-max-4xl'
      }
      hideCloseButton={
        activeScreen === 'successScreen' ||
        activeScreen === 'unsavedChangesScreen' ||
        activeScreen === 'errorScreen'
      }
    >
      {isFormScreen && activeScreen !== 'errorScreen' && (
        <Dialog.Title>{t('notificationRules.edit')}</Dialog.Title>
      )}

      {isNotificationTypesEnabled && isFormScreen && activeScreen !== 'errorScreen' && (
        <NotificationDialogProgressIndicator
          activeScreen={activeScreen}
          onStepClick={handleStepClick}
        />
      )}

      {isFormScreen && (
        <NotificationRuleDialogContent
          ref={dialogContentRef}
          handleFormSubmit={handleFormSubmit}
          isLoading={isLoading}
          requestError={requestError}
          formMethods={formMethods}
          initialScreen={activeScreen}
          onScreenChange={setActiveScreen}
        />
      )}

      {activeScreen === 'successScreen' && (
        <SuccessScreen
          mode={NotificationRuleMode.EDIT}
          notiRule={formMethods.getValues()}
          notificationRuleId={data.id}
        />
      )}

      {activeScreen === 'unsavedChangesScreen' && (
        <UnsavedDialog onContinueEditing={handleContinueEditing} />
      )}
    </Dialog>
  );
}
